import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Container, Box, Typography, Alert, Button, TextField, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useTranslation } from 'react-i18next';
import { usePrices } from '../hooks/usePrices';
import { useCoins } from '../contexts/CoinsContext';
import debounce from 'lodash.debounce';
import api from '../services/api';
import { useNavigate, useLocation } from 'react-router-dom';
import TokenIcon from '../components/TokenIcon';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useBlockchains } from '../contexts/BlockchainProvider';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { useWalletSync } from '../hooks/useWalletSync';
import SyncProgress from '../components/SyncProgress';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';

const now = new Date();
const pad = n => n.toString().padStart(2, '0');
const defaultInitialValues = {
  date: now.toISOString().slice(0, 10),
  hour: `${pad(now.getHours())}:${pad(now.getMinutes())}`,
  type: 'Buy',
  coin: '',
  blockchain: '',
  shares: '',
  costPerShare: '',
  fee: '0'
};

export default function AddHoldingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateWithLang } = useLanguageNavigation();
  const { coins, loading: coinsLoading, refreshCoins } = useCoins();
  const coinSymbols = useMemo(() => Array.isArray(coins) ? coins.map(coin => coin.symbol).filter(Boolean) : [], [coins]);
  const { prices: currentPrices, refresh: refreshPrices } = usePrices(coinSymbols, false, 'AddHoldingsPage');
  const { blockchains, loading: blockchainsLoading } = useBlockchains();
  const { syncWallet, syncProgress, error: syncError, setSyncProgress } = useWalletSync();

  // Tab state
  const [tab, setTab] = useState('wallet');

  // Add Exchange form state
  const [newExchange, setNewExchange] = useState({ 
    blockchain: 'kucoin', 
    apiKey: '', 
    apiSecret: '', 
    passphrase: '', 
    label: '' 
  });
  const [exchangeError, setExchangeError] = useState(null);
  const [exchangeSubmitting, setExchangeSubmitting] = useState(false);

  // Add Wallet form state
  const [newWallet, setNewWallet] = useState({ blockchain: '', address: '', label: '' });
  const [walletError, setWalletError] = useState(null);
  const [walletSubmitting, setWalletSubmitting] = useState(false);

  // Map user selection to backend canonical blockchain value for wallet creation
  const backendBlockchainMap = {
    sol: 'solana',
    solana: 'solana',
    btc: 'bitcoin',
    bitcoin: 'bitcoin',
    eth: 'ethereum',
    ethereum: 'ethereum',
    xrpl: 'xrpl',
    xrp: 'xrpl'
  };

  // Add Exchange submit handler
  const handleExchangeSubmit = async (e) => {
    e.preventDefault();
    setExchangeError(null);
    
    // Validate based on blockchain
    if (newExchange.blockchain === 'kraken') {
      if (!newExchange.apiKey || !newExchange.apiSecret) {
        setExchangeError('API Key and API Secret are required for Kraken.');
        return;
      }
    } else if (newExchange.blockchain === 'binance') {
      if (!newExchange.apiKey || !newExchange.apiSecret) {
        setExchangeError('API Key and API Secret are required for Binance.');
        return;
      }
    } else if (newExchange.blockchain === 'bitrue') {
      if (!newExchange.apiKey || !newExchange.apiSecret) {
        setExchangeError('API Key and API Secret are required for Bitrue.');
        return;
      }
    } else {
      // KuCoin requires passphrase
      if (!newExchange.apiKey || !newExchange.apiSecret || !newExchange.passphrase) {
        setExchangeError('API Key, API Secret, and Passphrase are required for KuCoin.');
        return;
      }
    }
    
    setExchangeSubmitting(true);
    // Show sync status immediately
    setSyncProgress({ status: 'starting', message: 'tables.wallets.sync.starting' });
    try {
      const blockchain = newExchange.blockchain || 'kucoin';
      console.log(`[AddHoldingsPage] Submitting exchange for blockchain=${blockchain}`);
      
      // Format address based on blockchain
      let address;
      if (blockchain === 'kraken') {
        // Kraken: "apiKey:apiSecret"
        address = `${newExchange.apiKey}:${newExchange.apiSecret}`;
      } else if (blockchain === 'binance') {
        // Binance: "apiKey:apiSecret"
        address = `${newExchange.apiKey}:${newExchange.apiSecret}`;
      } else if (blockchain === 'bitrue') {
        // Bitrue: "apiKey:apiSecret"
        address = `${newExchange.apiKey}:${newExchange.apiSecret}`;
      } else {
        // KuCoin: "apiKey:apiSecret:passphrase"
        address = `${newExchange.apiKey}:${newExchange.apiSecret}:${newExchange.passphrase}`;
      }
      
      // Add exchange as a wallet
      const resp = await api.post('/api/wallets', {
        blockchain: blockchain,
        address: address,
                        label: newExchange.label || `${blockchains.find(b => b.symbol.toLowerCase() === blockchain.toLowerCase())?.name || blockchain} Exchange`
      });
      const wallet = resp.data.wallet;
      console.log(`[AddHoldingsPage] Exchange added:`, wallet);
      
      // Reset exchange form immediately after successful creation
      setNewExchange({ 
        blockchain: 'kucoin', 
        apiKey: '', 
        apiSecret: '', 
        passphrase: '', 
        label: '' 
      });
      setExchangeError(null);
      
      // If exchange was added, trigger sync and show progress
      if (wallet && wallet.id) {
        try {
          // Start sync and mark navigation as pending
          setNavigationPending(true);
          syncWallet(wallet.id, null, address, blockchain);
          
          // Set timeout fallback for navigation
          setTimeout(() => {
            if (navigationPending) {
              console.log('[AddHoldingsPage] Exchange sync timeout, stopping navigation wait');
              setNavigationPending(false);
              setExchangeSubmitting(false);
            }
          }, 30000); // 30 second timeout
        } catch (syncErr) {
          console.error('[AddHoldingsPage] Error starting exchange sync:', syncErr);
          setExchangeSubmitting(false); // <-- Also set here in case of sync error
          // syncError will be shown by UI
        }
      } else {
        setExchangeSubmitting(false); // <-- If exchange not added, re-enable button
      }
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.message || 'Failed to add exchange';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setExchangeError(errorMsg);
      setExchangeSubmitting(false); // <-- If error adding exchange, re-enable button
      console.error(`[AddHoldingsPage] Error adding exchange:`, err);
    }
  };

  // Add Wallet submit handler
  const handleWalletSubmit = async (e) => {
    e.preventDefault();
    setWalletError(null);
    if (!newWallet.blockchain || !newWallet.address) {
      setWalletError('Blockchain and address are required.');
      return;
    }
    setWalletSubmitting(true);
    // Show sync status immediately
    setSyncProgress({ status: 'starting', message: 'tables.wallets.sync.starting' });
    try {
      const blockchain = newWallet.blockchain || '';
      const blockchainToSend = backendBlockchainMap[blockchain.toLowerCase()] || blockchain.toLowerCase();
      console.log(`[AddHoldingsPage] Submitting wallet for blockchain=${blockchainToSend}`);
      // Add wallet
      const resp = await api.post('/api/wallets', {
        ...newWallet,
        blockchain: blockchainToSend,
      });
      const wallet = resp.data.wallet;
      console.log(`[AddHoldingsPage] Wallet added:`, wallet);
      
      // Reset wallet form immediately after successful creation
      setNewWallet({ blockchain: '', address: '', label: '' });
      setWalletError(null);
      
      // If wallet was added, trigger sync and show progress
      if (wallet && wallet.id) {
        try {
          // Start sync and mark navigation as pending
          setNavigationPending(true);
          syncWallet(wallet.id, null, wallet.address, blockchainToSend);
          
          // Set timeout fallback for navigation
          setTimeout(() => {
            if (navigationPending) {
              console.log('[AddHoldingsPage] Wallet sync timeout, stopping navigation wait');
              setNavigationPending(false);
              setWalletSubmitting(false);
            }
          }, 30000); // 30 second timeout
        } catch (syncErr) {
          console.error('[AddHoldingsPage] Error starting sync:', syncErr);
          setWalletSubmitting(false); // <-- Also set here in case of sync error
          // syncError will be shown by UI
        }
      } else {
        setWalletSubmitting(false); // <-- If wallet not added, re-enable button
      }
    } catch (err) {
      let errorMsg = err.response?.data?.error || err.message || 'Failed to add wallet';
      if (err.response?.data?.details) {
        errorMsg += ': ' + err.response.data.details;
      }
      setWalletError(errorMsg);
      setWalletSubmitting(false); // <-- If error adding wallet, re-enable button
      console.error(`[AddHoldingsPage] Error adding wallet:`, err);
    }
  };

  const [form, setForm] = useState({ ...defaultInitialValues });
  const [touched, setTouched] = useState({});
  const isInitializing = useRef(false);
  const [error, setError] = useState(null);
  // Add Missing Coin UI state
  const [missingCoinQuery, setMissingCoinQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addCoinLoading, setAddCoinLoading] = useState(false);
  const [addCoinError, setAddCoinError] = useState(null);
  const inputRef = useRef();
  // Add editing state for manual transaction
  const [editing, setEditing] = useState(false); // Set to true if editing an existing transaction

  // Auto-fill costPerShare with current price if empty (like original modal)
  useEffect(() => {
    if (form.coin && (!form.costPerShare || Number(form.costPerShare) === 0)) {
      const priceData = currentPrices[form.coin];
      const currentPrice = priceData?.usd ?? 0;
      if (currentPrice && currentPrice > 0) {
        setForm((prev) => ({ ...prev, costPerShare: currentPrice.toString() }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.coin, currentPrices]);

  // Debounced search for coins by symbol/name
  const doSearch = useRef(
    debounce(async (query) => {
      if (query.length < 2) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      setAddCoinError(null);
      setSearchResults([]);
      try {
        const resp = await fetch(`https://api.coingecko.com/api/v3/search?query=${encodeURIComponent(query)}`);
        const data = await resp.json();
        setSearchResults(data.coins || []);
      } catch (err) {
        setAddCoinError('Failed to search for coins.');
      } finally {
        setSearching(false);
      }
    }, 1000)
  ).current;

  useEffect(() => {
    if (missingCoinQuery.length >= 2) {
      doSearch(missingCoinQuery);
    } else {
      setSearchResults([]);
      setSearching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingCoinQuery]);

  // Add missing coin to backend
  const handleAddMissingCoin = async (coin) => {
    setAddCoinLoading(true);
    setAddCoinError(null);
    try {
      const resp = await api.post('/api/coins/add', {
        symbol: coin.symbol.toUpperCase(),
        name: coin.name,
        coingecko_id: coin.id,
        image_url: coin.large,
        blockchain: 'ethereum'
      });
      const result = resp.data;
      if (!result.success) throw new Error(result.error || 'Failed to add coin');
      await refreshCoins();
      setForm((prev) => ({ ...prev, coin: coin.symbol.toUpperCase() }));
      setSearchResults([]);
      setMissingCoinQuery('');
      if (inputRef.current) inputRef.current.blur();
    } catch (err) {
      setAddCoinError(err.message || 'Failed to add coin.');
    } finally {
      setAddCoinLoading(false);
    }
  };

  // Helper function to format numbers with commas for display only
  const formatNumberForDisplay = (value) => {
    if (!value) return '';
    if (/\.$/.test(value) || (value.match(/\./g) || []).length > 1) return value;
    if (value.includes('.')) return value;
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString();
  };

  // Helper function to parse formatted numbers
  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.replace(/,/g, '');
  };

  // Form logic
  const filteredCoins = useMemo(() => {
    if (!coins) return [];
    return coins.sort((a, b) => a.name.localeCompare(b.name));
  }, [coins]);

  const handleChange = useCallback((field, value) => {
    let processedValue = value;
    if (field === 'shares' || field === 'costPerShare' || field === 'fee') {
      processedValue = value.replace(/,/g, '');
    }
    setForm((prev) => ({ ...prev, [field]: processedValue }));
    setTouched((prev) => ({ ...prev, [field]: true }));
    if (field === 'coin' && value) {
      const priceData = currentPrices[value];
      const currentPrice = priceData?.usd ?? 0;
      if (currentPrice && currentPrice > 0) {
        setForm((prev) => ({ ...prev, costPerShare: currentPrice.toString() }));
      } else {
        setForm((prev) => ({ ...prev, costPerShare: '' }));
        refreshPrices();
      }
      const selectedCoin = filteredCoins.find(c => c.symbol === value);
      setForm((prev) => ({
        ...prev,
        blockchain: (selectedCoin && selectedCoin.blockchain) ? selectedCoin.blockchain : 'ethereum'
      }));
    }
  }, [currentPrices, refreshPrices, filteredCoins]);

  const validate = () => {
    const sharesNum = parseFloat(form.shares);
    const costPerShareNum = parseFloat(form.costPerShare);
    return (
      form.date &&
      form.hour &&
      form.type &&
      form.coin &&
      form.shares &&
      !isNaN(sharesNum) &&
      sharesNum > 0 &&
      form.costPerShare &&
      !isNaN(costPerShareNum) &&
      costPerShareNum > 0
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setError(null);
      try {
        // Prepare payload as in the original modal
        const payload = {
          ...form,
          amount: Number(form.shares),
          buy_price: Number(form.costPerShare),
          price: Number(form.costPerShare),
          shares: Number(form.shares),
          costPerShare: Number(form.costPerShare),
          fee: Number(form.fee) || 0,
          hour: form.hour
        };
        if (form.blockchain && form.blockchain.trim() !== '') {
          payload.blockchain = form.blockchain;
        }
        await api.post('/api/transactions', payload);
        // Show success message (optional: you can use a Snackbar or Alert)
        navigate('/manual-holdings');
      } catch (err) {
        let errorMsg = err.response?.data?.error || err.message || 'Failed to add transaction';
        if (err.response?.data?.details) {
          errorMsg += ': ' + err.response.data.details;
        }
        setError(errorMsg);
      }
    } else {
      setTouched({ date: true, hour: true, type: true, coin: true, shares: true, costPerShare: true });
      setError(t('common.pleaseFillAllFields'));
    }
  };

  const syncProgressRef = useRef(syncProgress);
  const [navigationPending, setNavigationPending] = useState(false);
  
  useEffect(() => {
    syncProgressRef.current = syncProgress;
    
    // Handle navigation when sync completes
    if (navigationPending && syncProgress?.status === 'completed') {
      console.log('[AddHoldingsPage] Sync completed, navigating to wallets page');
      setNavigationPending(false);
      setWalletSubmitting(false);
      setExchangeSubmitting(false);
      navigateWithLang('wallets');
    } else if (navigationPending && syncProgress?.status === 'error') {
      console.log('[AddHoldingsPage] Sync failed, staying on page');
      setNavigationPending(false);
      setWalletSubmitting(false);
      setExchangeSubmitting(false);
    }
  }, [syncProgress, navigationPending, navigateWithLang]);

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
          <a href="#" onClick={e => { e.preventDefault(); navigate(-1); }} style={{ color: 'inherit', textDecoration: 'none' }}>
            ← {t('common.back')}
          </a>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('portfolio.addHoldings')}
          </Typography>
        </Box>
      </Box>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        indicatorColor="primary"
        textColor="primary"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label={t('manualHoldings.addWallet')} value="wallet" />
        <Tab label={t('manualHoldings.addExchange')} value="exchange" />
        <Tab label={t('manualHoldings.addManualTransaction')} value="transaction" />
      </Tabs>
      {tab === 'wallet' ? (
        <>
          <SyncProgress syncProgress={syncProgress} onClose={() => {}} />
          <form onSubmit={handleWalletSubmit}>
            {walletError && <Alert severity="error" sx={{ mb: 2 }}>{walletError}</Alert>}
            {syncError && <Alert severity="error" sx={{ mb: 2 }}>{syncError}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl fullWidth required error={Boolean(walletError && !newWallet.blockchain)}>
                <InputLabel>{t('common.blockchain')}</InputLabel>
                <Select
                  value={newWallet.blockchain}
                  label={t('common.blockchain')}
                  onChange={e => setNewWallet(prev => ({ ...prev, blockchain: e.target.value }))}
                  disabled={blockchainsLoading}
                >
                  {blockchains.map((chain, index) => (
                    <MenuItem 
                      key={chain.symbol || `blockchain-${index}`} 
                      value={(chain.symbol || '').toLowerCase()}
                      disabled={!chain.is_supported}
                      sx={{
                        opacity: chain.is_supported ? 1 : 0.5,
                        color: chain.is_supported ? 'inherit' : 'text.disabled'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img 
                          src={chain.icon_url} 
                          alt={chain.name} 
                          style={{ 
                            width: 24, 
                            height: 24,
                            opacity: chain.is_supported ? 1 : 0.5
                          }} 
                        />
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <span>{chain.name}</span>
                          {!chain.is_supported && (
                            <span style={{ 
                              fontSize: '0.75rem', 
                              color: 'text.disabled',
                              fontStyle: 'italic'
                            }}>
                              {t('settings.comingSoon')}
                            </span>
                          )}
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('common.address')}
                value={newWallet.address}
                onChange={e => setNewWallet(prev => ({ ...prev, address: e.target.value }))}
                fullWidth
                required
                error={Boolean(walletError && !newWallet.address)}
                helperText={walletError && !newWallet.address ? t('common.enterAddress') : ''}
              />
              <TextField
                label={t('common.walletLabel')}
                value={newWallet.label}
                onChange={e => setNewWallet(prev => ({ ...prev, label: e.target.value }))}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={() => navigate(-1)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 100 }} disabled={walletSubmitting}>
                {t('common.addWallet')}
              </Button>
            </Box>
          </form>
        </>
      ) : tab === 'exchange' ? (
        <>
          <SyncProgress syncProgress={syncProgress} onClose={() => {}} />
          <form onSubmit={handleExchangeSubmit}>
            {exchangeError && <Alert severity="error" sx={{ mb: 2 }}>{exchangeError}</Alert>}
            {syncError && <Alert severity="error" sx={{ mb: 2 }}>{syncError}</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
              <FormControl fullWidth required error={Boolean(exchangeError && !newExchange.blockchain)}>
                <InputLabel>{t('common.blockchain')}</InputLabel>
                <Select
                  value={newExchange.blockchain}
                  label={t('common.blockchain')}
                  onChange={e => {
                    const blockchain = e.target.value;
                    setNewExchange(prev => ({ 
                      ...prev, 
                      blockchain,
                      // Clear passphrase when switching to Kraken, Binance, or Bitrue (not needed)
                      passphrase: blockchain === 'kraken' || blockchain === 'binance' || blockchain === 'bitrue' ? '' : prev.passphrase
                    }));
                  }}
                  disabled={exchangeSubmitting}
                >
                  {blockchains
                    .filter(blockchain => ['kucoin', 'kraken', 'binance', 'bitrue'].includes(blockchain.symbol.toLowerCase()))
                    .map((blockchain) => (
                      <MenuItem key={blockchain.symbol} value={blockchain.symbol}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img 
                            src={blockchain.icon_url} 
                            alt={blockchain.name} 
                            style={{ width: 24, height: 24 }} 
                          />
                          <span>{blockchain.name}</span>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <TextField
                label="API Key"
                value={newExchange.apiKey}
                onChange={e => setNewExchange(prev => ({ ...prev, apiKey: e.target.value }))}
                fullWidth
                required
                error={Boolean(exchangeError && !newExchange.apiKey)}
                helperText={exchangeError && !newExchange.apiKey ? 'API Key is required' : ''}
                disabled={exchangeSubmitting}
              />
              <TextField
                label="API Secret"
                value={newExchange.apiSecret}
                onChange={e => setNewExchange(prev => ({ ...prev, apiSecret: e.target.value }))}
                fullWidth
                required
                error={Boolean(exchangeError && !newExchange.apiSecret)}
                helperText={exchangeError && !newExchange.apiSecret ? 'API Secret is required' : ''}
                type="password"
                disabled={exchangeSubmitting}
              />
              {newExchange.blockchain === 'kucoin' && (
                <TextField
                  label="Passphrase"
                  value={newExchange.passphrase}
                  onChange={e => setNewExchange(prev => ({ ...prev, passphrase: e.target.value }))}
                  fullWidth
                  required
                  error={Boolean(exchangeError && !newExchange.passphrase)}
                  helperText={exchangeError && !newExchange.passphrase ? 'Passphrase is required' : ''}
                  type="password"
                  disabled={exchangeSubmitting}
                />
              )}
              <TextField
                label={t('common.walletLabel')}
                value={newExchange.label}
                onChange={e => setNewExchange(prev => ({ ...prev, label: e.target.value }))}
                fullWidth
                placeholder={
                  blockchains.find(b => b.symbol.toLowerCase() === newExchange.blockchain.toLowerCase())?.name 
                    ? `${blockchains.find(b => b.symbol.toLowerCase() === newExchange.blockchain.toLowerCase())?.name} Exchange`
                    : 'Exchange Label'
                }
                disabled={exchangeSubmitting}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={() => navigate(-1)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 100 }} disabled={exchangeSubmitting}>
                {exchangeSubmitting ? t('manualHoldings.addingExchange') : t('manualHoldings.addExchange')}
              </Button>
            </Box>
          </form>
        </>
      ) : (
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label={t('common.date')}
                type="date"
                value={form.date}
                onChange={e => handleChange('date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(touched.date && !form.date)}
                helperText={touched.date && !form.date ? t('common.pleaseSelectDate') : ''}
                sx={{ flex: 1 }}
              />
              <TextField
                label={t('common.hour')}
                type="time"
                value={form.hour}
                onChange={e => handleChange('hour', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
                error={Boolean(touched.hour && !form.hour)}
                helperText={touched.hour && !form.hour ? t('common.pleaseSelectHour') : ''}
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControl fullWidth required error={Boolean(touched.type && !form.type)}>
              <InputLabel>{t('common.transactionType')}</InputLabel>
              <Select
                value={form.type}
                label={t('common.transactionType')}
                onChange={e => handleChange('type', e.target.value)}
              >
                <MenuItem value="Buy">{t('common.buy')}</MenuItem>
                <MenuItem value="Sell">{t('common.sell')}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required error={Boolean(touched.coin && !form.coin)}>
              <InputLabel>{t('common.coin')}</InputLabel>
              <Select
                value={form.coin}
                label={t('common.coin')}
                onChange={e => handleChange('coin', e.target.value)}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      zIndex: 99999
                    }
                  }
                }}
                disabled={editing}
              >
                {filteredCoins.map(coin => (
                  <MenuItem key={coin.symbol} value={coin.symbol}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <TokenIcon coinSymbol={coin.symbol} size={24} />
                      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <span>{coin.name} ({coin.symbol})</span>
                        {coin.blockchain && (
                          <span style={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            {coin.blockchain.toUpperCase()}
                          </span>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Add Missing Coin UI */}
            <Box sx={{ mt: 1, mb: 2, p: 1, border: '1px dashed #ccc', borderRadius: 1 }}>
              <Typography variant="subtitle2">{t('common.addMissingCoin')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <TextField
                  size="small"
                  label={t('common.coinSymbolOrName')}
                  value={missingCoinQuery}
                  onChange={e => setMissingCoinQuery(e.target.value)}
                  disabled={addCoinLoading}
                  sx={{ flex: 1 }}
                  inputRef={inputRef}
                />
              </Box>
              {addCoinError && <Alert severity="error" sx={{ mt: 1 }}>{addCoinError}</Alert>}
              {searchResults.length > 0 && (
                <ClickAwayListener onClickAway={() => { setSearchResults([]); setMissingCoinQuery(''); }}>
                  <Box sx={{ mt: 1 }}>
                    {searchResults.map(coin => (
                      <Box key={coin.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <img src={coin.large} alt={coin.symbol} width={24} height={24} style={{ borderRadius: 12 }} />
                        <span>{coin.name} ({coin.symbol.toUpperCase()})</span>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddMissingCoin(coin)}
                          disabled={addCoinLoading}
                        >
                          {addCoinLoading ? t('common.adding') : t('common.add')}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </ClickAwayListener>
              )}
            </Box>
            <TextField
              label={t('common.amount')}
              value={formatNumberForDisplay(form.shares)}
              onChange={e => handleChange('shares', parseFormattedNumber(e.target.value))}
              required
              error={Boolean(touched.shares && (!form.shares || isNaN(parseFormattedNumber(form.shares)) || Number(parseFormattedNumber(form.shares)) <= 0))}
              helperText={
                touched.shares && (!form.shares || isNaN(parseFormattedNumber(form.shares)) || Number(parseFormattedNumber(form.shares)) <= 0)
                  ? t('common.pleaseEnterValidNumberOfShares')
                  : ''
              }
              fullWidth
            />
            <TextField
              label={t('common.buyPricePerCoin')}
              value={formatNumberForDisplay(form.costPerShare)}
              onChange={e => handleChange('costPerShare', parseFormattedNumber(e.target.value))}
              required
              error={Boolean(touched.costPerShare && (!form.costPerShare || isNaN(parseFormattedNumber(form.costPerShare)) || Number(parseFormattedNumber(form.costPerShare)) <= 0))}
              helperText={
                touched.costPerShare && (!form.costPerShare || isNaN(parseFormattedNumber(form.costPerShare)) || Number(parseFormattedNumber(form.costPerShare)) <= 0)
                  ? t('common.pleaseEnterValidBuyPricePerCoin')
                  : ''
              }
              fullWidth
            />
            <TextField
              label={t('common.fee')}
              value={form.fee}
              onChange={e => handleChange('fee', e.target.value)}
              fullWidth
              error={Boolean(touched.fee && (form.fee && (isNaN(Number(form.fee)) || Number(form.fee) < 0)))}
              helperText={
                touched.fee && (form.fee && (isNaN(Number(form.fee)) || Number(form.fee) < 0))
                  ? t('common.pleaseEnterValidFeeAmount')
                  : t('common.optionalEnterTransactionFees')
              }
            />
          </Box>
          {/* Live Value Calculation */}
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            {(() => {
              const sharesNum = parseFloat(parseFormattedNumber(form.shares));
              const costNum = parseFloat(parseFormattedNumber(form.costPerShare));
              const value = (!isNaN(sharesNum) && !isNaN(costNum)) ? sharesNum * costNum : '';
              return (
                <span style={{ fontWeight: 600, fontSize: '1rem' }}>
                  {t('common.value')}: {value === '' ? '-' : value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              );
            })()}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
            <Button onClick={() => navigate(-1)} color="secondary" variant="outlined" sx={{ minWidth: 100 }}>{t('common.cancel')}</Button>
            <Button type="submit" variant="contained" color="primary" sx={{ minWidth: 100 }} disabled={!validate()}>{editing ? t('manualHoldings.updateTransaction') : t('common.addTransaction')}</Button>
          </Box>
        </form>
      )}
    </Container>
  );
} 